import type { Annotation } from "@/types/annotation";
import { Prediction } from "@utils/api/inference";
import { Thumbnail } from "./CardThumbnails";

/**
 * Filters items (annotations or predictions) to only include those from the specified file
 */
export function filterByFileId<T extends { fileId?: string }>(
    items: T[],
    currentFileId: string | undefined
): T[] {
    return items.filter((item) => item?.fileId === currentFileId);
}

/**
 * Checks if any annotations with the given label exist in files other than the current file
 */
export function hasAnnotationsInOtherFiles(
    allAnnotations: (Annotation & { fileId: string })[],
    currentFileId: string | undefined
): boolean {
    return allAnnotations.some((anno) => anno?.fileId !== currentFileId);
}

/**
 * Converts annotations to thumbnail objects for display
 */
export function annotationsToThumbnails(
    annotations: (Annotation & { frameId: string; fileId: string })[]
): Thumbnail[] {
    return annotations
        .filter((anno) => anno?.thumbnailDataUrl)
        .map((anno) => ({
            thumbnailDataUrl: anno.thumbnailDataUrl!,
            id: anno.id,
            frameId: anno.frameId,
            fileId: anno.fileId
        }));
}

/**
 * Converts predictions to thumbnail objects for display
 */
export function predictionsToThumbnails(predictions: Prediction[]): Thumbnail[] {
    return predictions
        .filter((prediction) => prediction?.thumbnailDataUrl)
        .map((prediction) => ({
            thumbnailDataUrl: prediction.thumbnailDataUrl!,
            id: prediction.detection_id,
            fileId: prediction.fileId,
            frameId: prediction.frameId
        }));
}

/**
 * Deletes annotations with the given label from the annotations map.
 * If annotations exist in other files, only deletes from the current file.
 * If annotations only exist in the current file, deletes from all files.
 */
export function deleteAnnotationsByLabel<T extends { label: string }>(
    annotationsMap: Record<string, Record<string, T[]>>,
    labelName: string,
    currentFileId: string | undefined
): Record<string, Record<string, T[]>> {
    const updated = { ...annotationsMap };

    // Check if annotations with this label exist in other files
    const hasAnnotationsInOtherFiles = Object.keys(updated).some((fileId) => {
        if (fileId === currentFileId) return false; // Skip current file
        return Object.keys(updated[fileId] || {}).some((frameId) => {
            return updated[fileId][frameId].some((annotation) => annotation.label === labelName);
        });
    });

    if (hasAnnotationsInOtherFiles) {
        // If annotations exist in other files, only delete from current file
        if (currentFileId && updated[currentFileId]) {
            Object.keys(updated[currentFileId]).forEach((frameId) => {
                updated[currentFileId][frameId] = updated[currentFileId][frameId].filter(
                    (annotation) => annotation.label !== labelName
                );
            });
        }
    } else {
        // If annotations only exist in current file, delete from all files
        Object.keys(updated).forEach((fileId) => {
            Object.keys(updated[fileId] || {}).forEach((frameId) => {
                updated[fileId][frameId] = updated[fileId][frameId].filter(
                    (annotation) => annotation.label !== labelName
                );
            });
        });
    }

    return updated;
}
